import React, { Suspense, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer'; // Import the useIdleTimer hook
import { Box, Button, Modal } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import Sidebar from '../../components/static/sidebar';
import { persistor, resetStore } from '../../store/store';
import { endSession } from "../../services/http-client";
import Spinner from '../spinner/spinner';
import './dashboardLayout.scss';
import { setSelectedShopsData } from '../../services/slices/selectedShops';
import { setActiveSession } from '../../services/slices/authTokenSlice';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const urlParams = new URLSearchParams(location.search);
  const jwt = urlParams.get('jwt');
  const CurrentLogCondition = useRef(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isLogout, setIsLogout] = useState()
  const [counter, setCounter] = useState(0)
  const { userdetails } = useSelector(state => state.userDetails);
  const { visualizeInfo } = useSelector((state) => (state?.authToken));
  const { authTokenResponseData, logoutSession } = useSelector((state) => state.authToken);


  const clearAllReduxStates = () => {
    dispatch(resetStore());
  }

  const purgePersistor = () => {
    return new Promise((resolve) => {
      persistor.purge();
      resolve();
    });
  };

  // Clear Session Storage
  const clearSessionStorage = () => {
    return new Promise((resolve) => {
      sessionStorage.clear();
      resolve();
    });
  };

// Clear Local Storage
  const clearLocalStorage = () => {
    return new Promise((resolve) => {
      localStorage.clear();
      resolve();
    });
  };

  // Logout Session from browser
  const sessionLogOut = useCallback(() => {
    endSession();
    clearAllReduxStates();
    purgePersistor();
    clearSessionStorage();
    clearLocalStorage();
    navigate("/", { replace: true });
    // eslint-disable-next-line
  }, [navigate]);


  useEffect(() => {
    dispatch(setSelectedShopsData([]))
  }, [dispatch])

  useEffect(() => {
    if (counter !== 0) {
      const intervalId = setInterval(() => {
        setCounter((prevSeconds) => prevSeconds - 1);
      }, 1000);

      return () => clearInterval(intervalId);
    } else if (modalIsOpen) {
      setTimeout(setIsLogout(true), 10000)
    }
  }, [counter, modalIsOpen, sessionLogOut]);

  useIdleTimer({
    timeout: 3600000,
    onIdle: async () => {
      setModalIsOpen(true);
      setCounter(10)
      if (!CurrentLogCondition.current && modalIsOpen) {
        setTimeout(setIsLogout(true), 10000)
      }
    },
    debounce: 500,
  });

  // Logout user
  const logOut = () => {
    // End the server session too, or its refresh cookie would keep it alive
    endSession();
    clearAllReduxStates();
    purgePersistor();
    clearSessionStorage();
    clearLocalStorage();
    navigate("/", { replace: true });
    setModalIsOpen(false);
    if (jwt) {
      navigate("/")
    }
  }

  const stayLoggedIn = () => {
    setModalIsOpen(false);
    CurrentLogCondition.current = true
  };

  const handleActiveSession = () => {
    dispatch(setActiveSession());
  }

  return (
    <div className="dashboard_container">
      <Sidebar />
      <Suspense fallback={<Spinner />}>
        <div className={!visualizeInfo ? "main-page overhidden" : "main-page"}>
          {userdetails?.userExpired && (
            <div className="scrolling-text-container">
              <span className="ac-status-bar">
                Your account has been expired. To activate your account, please contact the administrator.
              </span>
            </div>
          )}
          <Outlet />
        </div>
      </Suspense>
      <div>
        <Modal backdrop={"static"} open={!!modalIsOpen || !!authTokenResponseData?.activeSessions || !!logoutSession} className='common-modal'>
          <Box className="timeout-popup-box">
            <div className='timeout-title'>
              {!!authTokenResponseData?.activeSessions ?
                <h2><AccessTimeIcon /> Active Sessions</h2>
                :
                <h2><AccessTimeIcon /> {isLogout ? "Your session has expired" : "Session Expire Warning"}</h2>
              }
            </div>

            <div className='timeout-content'>
              {!!authTokenResponseData?.activeSessions ?
                <>
                  <p>Your account will be logged out from existing active sessions.</p>
                  <div className="d-flex justyfy-end">
                    <Button onClick={handleActiveSession} className="primary_button" >Ok</Button>
                  </div>
                </>
                :
                <>
                  {isLogout || !!logoutSession ?
                    <>
                      <p>{`You will be redirected to login page.${!!logoutSession ? "Your account has been logged in from another device.":""}`} </p>
                      <div className="d-flex justyfy-end">
                        <Button onClick={logOut} className="primary_button" >Ok</Button>
                      </div>
                    </>
                    :
                    <>
                      <p>Your session will expire in {counter} seconds. Please click continue to stay connected with us</p>
                      <div className="d-flex justyfy-end">
                        <Button onClick={stayLoggedIn} className="primary_button">Continue</Button>
                      </div>
                    </>
                  }
                </>
              }
            </div>
          </Box>
        </Modal>
        {/* )} */}
      </div>
    </div>
  );
};

export default memo(DashboardLayout);